import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import {
  CheckboxGroupInput,
  DateInput,
  Form,
  Loading,
  NumberInput,
  SelectArrayInput,
  SelectInput,
  TextInput,
  number,
  required,
  useDataProvider,
  useGetIdentity,
  useGetList,
  useGetOne,
  useNotify,
  useRedirect,
  minValue,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  CHANNEL_TYPE_LABELS,
  CHANNEL_TYPE_VALUES,
  type AdminDataProvider,
  type ChannelType,
  type RateRule,
  type RuleScopeRef,
  type RuleScopeType,
  type StoreTag,
  type UserTier,
} from '../dataProvider';
import { RequireAccess } from '../auth/RequireAccess';
import { DEFAULT_PRIORITY, scopeRequiresWindow } from './rateRuleState';
import { RuleScopePreview } from './RuleScopePreview';
import { RuleConflictPreview } from './RuleConflictPreview';
import type { CandidateRule } from './candidateRule';

/**
 * The rate-rule authoring form (D-rate-rules) — the visual scope builder plus the
 * two honest previews. The scopeType picker drives which scopeRef selector shows
 * and PREFILLS the suggested priority (editable, required, never a silent 0).
 * Marketplace and category scopes REQUIRE a window (a permanent broad rule belongs
 * in the base rates). The form authors a rule in DRAFT; going live is the separate
 * activate action (the rate-affecting elevation). Both previews read the ONE
 * mirrored resolver and are labelled projections over today's catalog and rules.
 */

const SCOPE_CHOICES: { id: RuleScopeType; name: string }[] = [
  { id: 'store', name: 'Store' },
  { id: 'marketplace', name: 'Marketplace' },
  { id: 'category', name: 'Category' },
  { id: 'marketplace_category', name: 'Marketplace in category' },
  { id: 'tag', name: 'Tag' },
  { id: 'tag_marketplaces', name: 'Tag on marketplaces' },
  { id: 'store_list', name: 'Store list' },
  { id: 'market', name: 'Market' },
];

const EFFECT_CHOICES = [
  { id: 'multiplier', name: 'Multiplier (x base)' },
  { id: 'absolute', name: 'Set rate' },
  { id: 'cap', name: 'Cap (ceiling)' },
  { id: 'floor', name: 'Floor' },
];

const MARKET_CHOICES = [
  { id: 'uk', name: 'UK' },
  { id: 'us', name: 'US' },
  { id: 'ie', name: 'IE' },
  { id: 'all', name: 'All markets' },
];

const CHANNEL_CHOICES = CHANNEL_TYPE_VALUES.map((c) => ({
  id: c,
  name: CHANNEL_TYPE_LABELS[c],
}));

const MARKETPLACE_CHOICES = CHANNEL_TYPE_VALUES.filter(
  (c) => c !== 'direct' && c !== 'brick_and_mortar',
).map((c) => ({ id: c, name: CHANNEL_TYPE_LABELS[c] }));

interface FormValues {
  displayLabel: string;
  displayReasonCode: string;
  scopeType: RuleScopeType;
  scopeStore: number | null;
  scopeMarketplace: ChannelType | '';
  scopeCategory: number | null;
  scopeTag: string;
  scopeMarketplaces: ChannelType[];
  scopeStoreList: string;
  scopeMarket: string;
  effectType: string;
  effectValue: number | null;
  tierEligibility: UserTier[];
  market: string;
  priority: number | null;
  visibleFrom: string | null;
  visibleUntil: string | null;
}

/** Build the scopeRef from the flat fields, or null when the scope is incomplete. */
const buildScopeRef = (v: FormValues): RuleScopeRef | null => {
  switch (v.scopeType) {
    case 'store':
      return v.scopeStore != null ? Number(v.scopeStore) : null;
    case 'marketplace':
      return v.scopeMarketplace !== '' ? v.scopeMarketplace : null;
    case 'category':
      return v.scopeCategory != null ? Number(v.scopeCategory) : null;
    case 'marketplace_category':
      return v.scopeMarketplace !== '' && v.scopeCategory != null
        ? {
            channelType: v.scopeMarketplace,
            categoryId: Number(v.scopeCategory),
          }
        : null;
    case 'tag':
      return v.scopeTag !== '' ? v.scopeTag : null;
    case 'tag_marketplaces':
      return v.scopeTag !== '' && v.scopeMarketplaces.length > 0
        ? { tag: v.scopeTag, marketplaces: v.scopeMarketplaces }
        : null;
    case 'store_list': {
      const ids = (v.scopeStoreList ?? '')
        .split(',')
        .map((s) => Number(s.trim()))
        .filter((n) => Number.isFinite(n) && n > 0);
      return ids.length > 0 ? ids : null;
    }
    case 'market':
      return v.scopeMarket !== '' ? v.scopeMarket : null;
    default:
      return null;
  }
};

const toCandidate = (v: FormValues): CandidateRule | null => {
  const scopeRef = buildScopeRef(v);
  if (
    scopeRef == null ||
    v.effectValue == null ||
    v.priority == null ||
    v.tierEligibility.length === 0
  ) {
    return null;
  }
  return {
    scopeType: v.scopeType,
    scopeRef,
    effectType: v.effectType as CandidateRule['effectType'],
    effectValue: Number(v.effectValue),
    market: v.market as CandidateRule['market'],
    tierEligibility: v.tierEligibility,
    priority: Number(v.priority),
    displayLabel: v.displayLabel || null,
  };
};

/** The scopeRef inputs for the selected scopeType, plus the live previews. */
const ScopeBuilder = ({
  tagChoices,
  asOf,
}: {
  tagChoices: { id: string; name: string }[];
  asOf: number;
}) => {
  const { setValue } = useFormContext();
  const values = useWatch() as FormValues;
  const scopeType = values.scopeType;

  // Prefill the suggested priority when the scope type changes (the field stays
  // editable and required; the prefill never fires on the initial edit load).
  const prevScope = useRef(scopeType);
  useEffect(() => {
    if (prevScope.current !== scopeType) {
      prevScope.current = scopeType;
      setValue('priority', DEFAULT_PRIORITY[scopeType]);
    }
  }, [scopeType, setValue]);

  const candidate = toCandidate(values);

  return (
    <>
      <SelectInput
        source="scopeType"
        label="Scope"
        choices={SCOPE_CHOICES}
        validate={required()}
      />

      {scopeType === 'store' && (
        <NumberInput source="scopeStore" label="Store (merchant region id)" />
      )}
      {(scopeType === 'marketplace' ||
        scopeType === 'marketplace_category') && (
        <SelectInput
          source="scopeMarketplace"
          label="Marketplace"
          choices={CHANNEL_CHOICES}
        />
      )}
      {(scopeType === 'category' || scopeType === 'marketplace_category') && (
        <NumberInput source="scopeCategory" label="Category id" />
      )}
      {(scopeType === 'tag' || scopeType === 'tag_marketplaces') && (
        <SelectInput source="scopeTag" label="Tag" choices={tagChoices} />
      )}
      {scopeType === 'tag_marketplaces' && (
        <SelectArrayInput
          source="scopeMarketplaces"
          label="Marketplaces"
          choices={MARKETPLACE_CHOICES}
        />
      )}
      {scopeType === 'store_list' && (
        <TextInput
          source="scopeStoreList"
          label="Store ids (comma separated)"
          helperText="e.g. 101, 102, 103"
          fullWidth
        />
      )}
      {scopeType === 'market' && (
        <SelectInput
          source="scopeMarket"
          label="Market (the scope)"
          choices={MARKET_CHOICES.filter((m) => m.id !== 'all')}
        />
      )}

      <Divider sx={{ my: 2 }} />
      {candidate ? (
        <Stack spacing={2}>
          <RuleScopePreview candidate={candidate} />
          <RuleConflictPreview candidate={candidate} now={asOf} />
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Complete the scope, effect, tier and priority to see the previews.
        </Typography>
      )}
    </>
  );
};

const RateRuleFormInner = ({
  record,
  asOf,
}: {
  record?: RateRule;
  asOf: number;
}) => {
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const redirect = useRedirect();
  const { identity } = useGetIdentity();
  const { data: tags = [] } = useGetList<StoreTag>('storeTags', {
    pagination: { page: 1, perPage: 200 },
    sort: { field: 'label', order: 'ASC' },
  });
  const tagChoices = tags.map((t) => ({ id: t.key, name: t.label }));

  const isEdit = record != null;
  const ref = record?.scopeRef;
  const defaultValues: FormValues = {
    displayLabel: record?.displayLabel ?? '',
    displayReasonCode: record?.displayReasonCode ?? '',
    scopeType: record?.scopeType ?? 'store',
    scopeStore: record?.scopeType === 'store' ? (ref as number) : null,
    scopeMarketplace:
      record?.scopeType === 'marketplace'
        ? (ref as ChannelType)
        : record?.scopeType === 'marketplace_category'
          ? (ref as { channelType: ChannelType }).channelType
          : '',
    scopeCategory:
      record?.scopeType === 'category'
        ? (ref as number)
        : record?.scopeType === 'marketplace_category'
          ? (ref as { categoryId: number }).categoryId
          : null,
    scopeTag:
      record?.scopeType === 'tag'
        ? (ref as string)
        : record?.scopeType === 'tag_marketplaces'
          ? (ref as { tag: string }).tag
          : '',
    scopeMarketplaces:
      record?.scopeType === 'tag_marketplaces'
        ? (ref as { marketplaces: ChannelType[] }).marketplaces
        : [],
    scopeStoreList:
      record?.scopeType === 'store_list' ? (ref as number[]).join(', ') : '',
    scopeMarket: record?.scopeType === 'market' ? (ref as string) : '',
    effectType: record?.effectType ?? 'multiplier',
    effectValue: record?.effectValue ?? null,
    tierEligibility: record?.tierEligibility ?? ['free', 'plus'],
    market: record?.market ?? 'all',
    priority: record?.priority ?? DEFAULT_PRIORITY.store,
    visibleFrom: record?.visibleFrom ?? null,
    visibleUntil: record?.visibleUntil ?? null,
  };

  const onSubmit = async (raw: Record<string, unknown>) => {
    const v = raw as unknown as FormValues;
    const scopeRef = buildScopeRef(v);
    if (scopeRef == null) {
      notify('Complete the scope for the chosen scope type', { type: 'error' });
      return;
    }
    if (v.tierEligibility.length === 0) {
      notify('Pick at least one tier', { type: 'error' });
      return;
    }
    // Marketplace and category scopes require a window (a permanent broad rule
    // belongs in the base rates, not a rule).
    if (
      scopeRequiresWindow(v.scopeType) &&
      (!v.visibleFrom || !v.visibleUntil)
    ) {
      notify('A marketplace or category rule needs a start and end date', {
        type: 'error',
      });
      return;
    }

    const fields = {
      scopeType: v.scopeType,
      scopeRef,
      effectType: v.effectType,
      effectValue: Number(v.effectValue),
      tierEligibility: v.tierEligibility,
      market: v.market,
      priority: Number(v.priority),
      visibleFrom: v.visibleFrom || null,
      visibleUntil: v.visibleUntil || null,
      displayLabel: v.displayLabel || null,
      displayReasonCode: v.displayReasonCode || null,
    };

    try {
      if (isEdit && record) {
        await dataProvider.update('rateRules', {
          id: record.id,
          data: { ...fields, updatedAt: new Date().toISOString() },
          previousData: record,
        });
        notify('Rule saved', { type: 'success' });
        redirect('list', 'rateRules');
      } else {
        const nowIso = new Date().toISOString();
        const { data } = await dataProvider.create('rateRules', {
          data: {
            ...fields,
            // A new rule is authored in DRAFT; going live is the separate activate act.
            status: 'draft',
            createdBy: String(identity?.email ?? identity?.id ?? 'unknown'),
            createdAt: nowIso,
            updatedAt: nowIso,
          },
        });
        notify('Rule created in draft', { type: 'success' });
        redirect('edit', 'rateRules', data.id);
      }
    } catch {
      notify('Could not save the rule', { type: 'error' });
    }
  };

  return (
    <Box sx={{ maxWidth: 760 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        {isEdit ? `Edit rate rule` : 'New rate rule'}
      </Typography>
      <Form defaultValues={defaultValues} onSubmit={onSubmit}>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <TextInput source="displayLabel" label="Label" fullWidth />
            <TextInput
              source="displayReasonCode"
              label="Reason code"
              fullWidth
            />
            <ScopeBuilder tagChoices={tagChoices} asOf={asOf} />
          </CardContent>
        </Card>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
              Effect
            </Typography>
            <SelectInput
              source="effectType"
              label="Effect"
              choices={EFFECT_CHOICES}
              validate={required()}
            />
            <NumberInput
              source="effectValue"
              label="Effect value"
              helperText="A multiplier factor (1.5) or a rate in percent"
              validate={[required(), number(), minValue(0)]}
            />
            <CheckboxGroupInput
              source="tierEligibility"
              label="Tiers"
              choices={[
                { id: 'free', name: 'Free' },
                { id: 'plus', name: 'Plus' },
              ]}
            />
            <SelectInput
              source="market"
              label="Market"
              choices={MARKET_CHOICES}
              validate={required()}
            />
            <NumberInput
              source="priority"
              label="Priority"
              helperText="Higher wins. Prefilled from the scope, editable, required."
              validate={[required(), number(), minValue(0)]}
            />
            <DateInput source="visibleFrom" label="Visible from" />
            <DateInput source="visibleUntil" label="Visible until" />
          </CardContent>
        </Card>

        <Stack direction="row" spacing={2}>
          <Button type="submit" variant="contained">
            {isEdit ? 'Save' : 'Create draft'}
          </Button>
          <Button type="button" onClick={() => redirect('list', 'rateRules')}>
            Cancel
          </Button>
        </Stack>
      </Form>
    </Box>
  );
};

/** Create view. Exported for testing without the access wrapper. */
export const RateRuleCreateView = ({ asOf }: { asOf?: number }) => {
  const [now] = useState<number>(() => asOf ?? Date.now());
  return <RateRuleFormInner asOf={now} />;
};

/** Edit view. Exported for testing without the access wrapper. */
export const RateRuleEditView = ({ asOf }: { asOf?: number }) => {
  const { id } = useParams();
  const [now] = useState<number>(() => asOf ?? Date.now());
  const { data: record, isLoading } = useGetOne<RateRule>('rateRules', {
    id: Number(id),
  });
  if (isLoading || !record) return <Loading />;
  return <RateRuleFormInner record={record} asOf={now} />;
};

export const RateRuleCreate = () => (
  <RequireAccess capability="rate_rules:author">
    <RateRuleCreateView />
  </RequireAccess>
);

export const RateRuleEdit = () => (
  <RequireAccess capability="rate_rules:author">
    <RateRuleEditView />
  </RequireAccess>
);
